// src/components/digital_twin/twin_engine/ui/AiExplainerTab.js
// FlowSense AI explainer for the asset open in the twin.
//   • Brief: rating, headline, key facts, drivers (with evidence), recommended actions, data gaps —
//     built from the data the drawer fetched (twinExplainer.buildBrief), so it is instant and offline.
//   • Ask: suggested questions answer locally at once; free text (and "Ask FlowSense AI" on a
//     suggestion) goes to the FlowSense AI service with the full asset context.
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Box, Button, Flex, HStack, Icon, IconButton, Input, InputGroup, InputRightElement, SimpleGrid, Spinner, Text, Tooltip, VStack, Wrap, WrapItem, useClipboard } from '@chakra-ui/react';
import { FaBrain, FaPaperPlane, FaCopy, FaExclamationTriangle, FaListOl, FaQuestionCircle, FaDatabase } from 'react-icons/fa';
import { askFlowSenseAi } from '../../../../services/flowsenseAiService';
import { ASSET_QUESTIONS, TIER_META, answerLocally, buildAiContext } from '../twinExplainer';
import { AI_GRADIENT, LevelBadge, Section, useTwinTokens } from './ui';

const Answer = memo(({ item }) => {
  const t = useTwinTokens();
  const { onCopy, hasCopied } = useClipboard(item.answer || '');
  return (
    <Box bg={t.raised} borderWidth="1px" borderColor={t.border} borderRadius="lg" p={3}>
      <Flex justify="space-between" align="center" mb={1} gap={2}>
        <Text fontSize="13px" fontWeight="800" color={t.text}>{item.q}</Text>
        <HStack spacing={1}>
          <Badge colorScheme={item.source === 'ai' ? 'purple' : 'cyan'} fontSize="9px">{item.source === 'ai' ? `FlowSense AI${item.model ? ` · ${item.model}` : ''}` : 'Rules · from twin data'}</Badge>
          <Tooltip label={hasCopied ? 'Copied' : 'Copy'} hasArrow><IconButton size="xs" variant="ghost" icon={<FaCopy />} aria-label="Copy answer" onClick={onCopy} /></Tooltip>
        </HStack>
      </Flex>
      {item.pending ? <HStack><Spinner size="xs" color="purple.400" /><Text fontSize="13px" color={t.muted}>Thinking with the asset context…</Text></HStack>
        : <Text fontSize="13px" whiteSpace="pre-wrap" color={item.error ? 'orange.400' : t.text}>{item.answer}</Text>}
    </Box>
  );
});
Answer.displayName = 'Answer';

function AiExplainerTab({ brief, ctx }) {
  const t = useTwinTokens();
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const mounted = useRef(true);
  const ctxRef = useRef(ctx); ctxRef.current = ctx;
  const briefRef = useRef(brief); briefRef.current = brief;
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => { setItems([]); }, [ctx.asset?.id]);
  const tier = TIER_META[brief.tier] || TIER_META.unknown;

  const askAi = useCallback(async (q) => {
    const id = `${Date.now()}-${Math.random()}`;
    setItems((xs) => [{ id, q, pending: true, source: 'ai' }, ...xs].slice(0, 12));
    try {
      const r = await askFlowSenseAi(q, buildAiContext(ctxRef.current, briefRef.current), 'digital_twin');
      const answer = r?.answer || r?.data?.answer || (typeof r === 'string' ? r : null);
      if (!answer) throw new Error('empty answer');
      if (mounted.current) setItems((xs) => xs.map((x) => (x.id === id ? { ...x, pending: false, answer, model: r?.model || r?.data?.model || null } : x)));
    } catch (e) {
      if (!mounted.current) return;
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, pending: false, source: 'local', error: true, answer: `FlowSense AI is unavailable (${e?.response?.status || e.message}). From the twin data:\n\n${answerLocally('why', briefRef.current, ctxRef.current)}` } : x)));
    }
  }, []);

  const askLocal = useCallback((qq) => {
    setItems((xs) => [{ id: `${qq.id}-${Date.now()}`, q: qq.q, answer: answerLocally(qq.id, briefRef.current, ctxRef.current), source: 'local' }, ...xs].slice(0, 12));
  }, []);

  const submit = () => { const q = text.trim(); if (!q) return; setText(''); askAi(q); };

  return (
    <VStack align="stretch" spacing={4}>
      <Box borderRadius="xl" p="1px" bgGradient={AI_GRADIENT}>
        <Box bg={t.card} borderRadius="xl" p={4}>
          <HStack spacing={2} mb={2}>
            <Icon as={FaBrain} color="purple.400" />
            <Text fontSize="sm" fontWeight="900">FlowSense AI brief</Text>
            <Badge colorScheme={tier.scheme} variant="solid" fontSize="10px">{tier.label}</Badge>
            <Tooltip label="Share of this asset's data sources that returned data" hasArrow><Badge variant="outline" fontSize="10px">data {brief.coverage}%</Badge></Tooltip>
          </HStack>
          <Text fontSize="15px" fontWeight="700" color={t.text}>{brief.headline}</Text>
          {brief.facts.length > 0 && (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={6} spacingY={1} mt={3}>
              {brief.facts.map((f) => (
                <Flex key={f.label} justify="space-between" gap={2} fontSize="13px"><Text color={t.muted}>{f.label}</Text><Text fontWeight="700" textAlign="right">{f.value}</Text></Flex>
              ))}
            </SimpleGrid>
          )}
        </Box>
      </Box>

      <Section title="Why — the evidence" icon={FaExclamationTriangle}>
        {brief.drivers.length ? brief.drivers.map((d, i) => (
          <Flex key={`${d.label}-${i}`} gap={2} py={1.5} align="flex-start">
            <LevelBadge level={d.level} mt="2px" flexShrink={0}>{d.level}</LevelBadge>
            <Box><Text fontSize="13px" fontWeight="700">{d.label}</Text>{d.detail && <Text fontSize="12px" color={t.muted}>{d.detail}</Text>}</Box>
          </Flex>
        )) : <Text fontSize="13px" color={t.muted}>No risk drivers in the current data.</Text>}
      </Section>

      <Section title="What to do" icon={FaListOl}>
        {brief.actions.length ? brief.actions.map((a, i) => (
          <Flex key={a.text} gap={2} py={1.5} align="flex-start">
            <Text fontSize="13px" fontWeight="900" color={t.muted} w="18px">{i + 1}.</Text>
            <Text fontSize="13px" flex={1}>{a.text}</Text>
            <LevelBadge level={a.priority}>{a.priority}</LevelBadge>
          </Flex>
        )) : <Text fontSize="13px" color={t.muted}>No action needed on the current data.</Text>}
      </Section>

      {brief.gaps.length > 0 && (
        <Section title="Data gaps" icon={FaDatabase}>
          {brief.gaps.map((g) => <Text key={g} fontSize="13px" color={t.muted} py={0.5}>• {g}</Text>)}
        </Section>
      )}

      <Section title="Ask about this asset" icon={FaQuestionCircle} accent>
        <Wrap spacing={2} mb={3}>
          {ASSET_QUESTIONS.map((qq) => (
            <WrapItem key={qq.id}>
              <HStack spacing={0}>
                <Button size="xs" variant="outline" colorScheme="cyan" borderRightRadius={0} onClick={() => askLocal(qq)}>{qq.q}</Button>
                <Tooltip label="Ask FlowSense AI instead" hasArrow><IconButton size="xs" colorScheme="purple" variant="outline" borderLeftRadius={0} icon={<FaBrain />} aria-label={`Ask FlowSense AI: ${qq.q}`} onClick={() => askAi(qq.q)} /></Tooltip>
              </HStack>
            </WrapItem>
          ))}
        </Wrap>
        <InputGroup size="md">
          <Input placeholder={`Ask FlowSense AI about ${brief.name}…`} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} fontSize="14px" />
          <InputRightElement><IconButton size="sm" colorScheme="purple" icon={<FaPaperPlane />} aria-label="Send question" onClick={submit} isDisabled={!text.trim()} /></InputRightElement>
        </InputGroup>
        <VStack align="stretch" spacing={2} mt={3}>{items.map((it) => <Answer key={it.id} item={it} />)}</VStack>
      </Section>
    </VStack>
  );
}

export default memo(AiExplainerTab);
